import { Injectable } from "@angular/core";
import { MassUploadEntryConfig, MassUploadEntryMap, EXCEL_PERSON_TYPES, MassUploadExcelToJsonMap, MassUploadSpouseDependentValidations } from "../interfaces/mass-upload-grid.interface";
import { EntryConfigInterface, FormConfig, FormNavigationConfig } from "@ptl/forms";
import { MASS_UPLOAD_STATE_CUSTOM } from "../reducer/mass-upload-grid.reducer";
import { brandValues } from "@ptl/brand";
import { MassUploadGridDataHelperService } from "./mug-data-helper.service";


@Injectable({
  providedIn: 'root'
})
export class MassUploadGridEntryMapTransformerService {

  constructor(private mugDataHelperService: MassUploadGridDataHelperService) {}

  /**
    @description loop through all pages and all entries arrays in formJson and 
    assign each entry to the map where the key is the formControlName and the value is the entry object

    Flattened to avoid having to do pages[0].entries...
  */
  flattenAllEntriesToMap(formJson: FormConfig<FormNavigationConfig>): MassUploadEntryMap {
    let entryMap = {};

    if (formJson?.pages) {
      formJson.pages.forEach(page => {
        if (page.entries) {
          page.entries.forEach(entry => {
            if (entry.formControlAttributes?.formControlName) {
              entryMap[entry.formControlAttributes.formControlName] = entry;
            }
          });
        }
      });
    }

    return entryMap;
  }

  addMassUploadAttributesToAllEntries(allEntryMap: MassUploadEntryMap) {
    Object.values(allEntryMap).forEach((entry) => {
      entry.massUploadAttributes = this.getDummyEntry().massUploadAttributes;
    })
  }

  addCustomFormEntries(allEntryMap: MassUploadEntryMap, formEntryOverrides: MassUploadEntryConfig[]) {
    formEntryOverrides.forEach((entryOverride: EntryConfigInterface, index) => {
      let formControlName = entryOverride?.formControlAttributes?.formControlName;

      if (!formControlName) {
        console.error('Missing form control name in entryOverrides at index ' + index);
        return;
      }

      // Here we merge the override objects with the existing entries.  Due to the complexity of the object
      // and the difficulty of a deep clone, we're merging just the top level objects like base, formControlAttributes, etc.
      // This preserves all of the important attributes so they are not overwritten by a blanket Object.assign call
      // TODO - do we have a standard of deep cloning objects? Ideally we would want to preserve EVERYTHING and only overwrite the provided properties
      let existingEntry = allEntryMap[formControlName] ?? this.getDummyEntry();
      Object.keys(entryOverride).forEach((overrideKey) => {
        existingEntry[overrideKey] = {...existingEntry[overrideKey], ...entryOverride[overrideKey]}
      });

      allEntryMap[formControlName] = existingEntry;
    });
  }

  /**
   * 
   * @description We usually want to maintain existing form listeners.
   * This function can be extended to include listeners that need to be duplicated many times.
   * Rather than override every single entry that needs it in the config file, this makes it easier to reuse them
   */
  setCustomListeners(entryCopy: MassUploadEntryConfig, mapItem: MassUploadExcelToJsonMap, stateId: number) {
    if (!entryCopy.base.formListeners?.length) {
      entryCopy.base.formListeners = [];
    }

    if (mapItem.disableForRegions?.length) {
      let oppositeRegions = brandValues.filter((brand) => !mapItem.disableForRegions.includes(brand.region)).map((brand) => brand.region);
      oppositeRegions.push(null);
      entryCopy.base.formListeners.push({
        helperId: stateId,
        listenToFormControls: ['planRegion'],
        triggers: [
          {
            'listenForValue': mapItem.disableForRegions,
            'targetProperty': 'base.display',
            'changePropertyValueTo': false
          },
          {
            'listenForValue': oppositeRegions,
            'targetProperty': 'base.display',
            'changePropertyValueTo': true
          }
        ]
      });
    }
  }

  setSpouseAndDependentValidations(
    entryCopy: MassUploadEntryConfig,
    mapItem: MassUploadExcelToJsonMap,
    validations: MassUploadSpouseDependentValidations,
    personType: EXCEL_PERSON_TYPES.DEPENDENT | EXCEL_PERSON_TYPES.SPOUSE
  ) {

    // No dependent relevance, disable
    if (!mapItem.dependentField) {
      entryCopy.formControlAttributes.disabled = true;
    }

    // Apply only for spouse, other dependents: disable
    else if (mapItem.dependentField_SpouseOnly && personType !== EXCEL_PERSON_TYPES.SPOUSE) {
      entryCopy.formControlAttributes.disabled = true;
    }

    // DOB logic
    else if (mapItem.formControlName === 'dob') {
      entryCopy.formControlAttributes.validators = [
        {type: 'required', value: true},
        {type: 'maxLength', value: 10},
        {type: 'minAgeManual', value: validations.minAge},
        {type: 'maxAgeManual', value: validations.maxAge}
      ];
      entryCopy.custom = {
        minAge: validations.minAge,
        maxAge: validations.maxAge
      }
    }
    // For dependents, we want to require the other address fields if this one is populated to ensure a full separate address
    else if (mapItem.formControlName === 'addressLine1') {
      if (!entryCopy.base.formBroadcast?.length) {
        entryCopy.base.formBroadcast = [];
      }

      entryCopy.base.formBroadcast.push({
        type: 'valueChange',
        signal: 'value'
      });

      entryCopy.formControlAttributes.validators = entryCopy.formControlAttributes.validators.filter((validator) => validator.type !== 'required');
    }
    else if (['addressLine2', 'city', 'zipCode', 'county', 'state'].includes(mapItem.formControlName)) {
      entryCopy.base.formListeners.push({
        listenToFormControls: ['addressLine1'],
        triggers: [
          {
            conditional: [
              {
                formControlNames: ['addressLine1'],
                comparator: 'is-empty-or-null',
              }
            ],
            targetProperty: 'base.display',
            onPassChangePropertyValueTo: false,
            onFailChangePropertyValueTo: true
          }
        ]
      });
    }
    else if (!mapItem.forceDisabled && mapItem.formControlName === 'tobaccoIndicator' && validations.minSmokingAge) {
      entryCopy.base.formListeners.push({
        triggers: [
          {
            conditional: [
              {
                formControlNames: ['dob'],
                comparator: 'is-age-less-than',
                comparatorValue: validations.minSmokingAge
              }
            ],
            targetProperty: 'base.display',
            onPassChangePropertyValueTo: false,
            onFailChangePropertyValueTo: true
          }
        ]
      });
    }
  }

  getDummyEntry(): MassUploadEntryConfig {
    return {
      order: 0,
      base: {
        componentType: 'mass-upload-grid-control',
        display: true,
      },
      formControlAttributes: {
        connectPath: MASS_UPLOAD_STATE_CUSTOM,
        formControlName: null
      },
      massUploadAttributes: {
        editMode: false,
      }
    }
  }

  getPlaceholderEntryConfig(formControlName: string): MassUploadEntryConfig {
    return {
      order: 0,
      base: {
        componentType: 'mass-upload-grid-control',
        display: false,
      },
      formControlAttributes: {
        connectPath: MASS_UPLOAD_STATE_CUSTOM,
        formControlName: formControlName
      },
      massUploadAttributes: {
        editMode: false
      }
    }
  }
}